import { SyntheticEvent, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import IEslTemplate from '../../models/esl/eslTemplate';
import IEslTemplateItem from '../../models/esl/eslTemplateElement';
import { EEslTemplateElementType } from '../../models/enums/eslTemplateType';
import eslTemplateService from '../../services/esl/EslTemplateService';
import RenderedElement from './canvas/RenderedElement';
import { getEslSignature } from './eslSignature';
import { colorForScreen, quantizeImageForScreen, EslScreenType } from './quantizeImage';
import { EslPreviewModeProvider } from './EslPreviewModeContext';

// Templates in the list view always render in the color-screen preview so
// admins see the as-rendered look while browsing. The editor opens in design
// mode and lets users toggle from there.
const LIST_PREVIEW_SCREEN: EslScreenType = 'colorScreen';
const SIGNATURE_HEIGHT = 28;

interface EslTemplatePreviewProps {
  template: IEslTemplate;
  scale?: number;
}

const EslTemplatePreview = ({ template, scale = 0.3 }: EslTemplatePreviewProps) => {
  const [resolvedElements, setResolvedElements] = useState<IEslTemplateItem[]>();
  const [signatureWidth, setSignatureWidth] = useState<number>(0);

  const sourceSignatureUrl = getEslSignature(template);
  const [signatureUrl, setSignatureUrl] = useState<string>(sourceSignatureUrl);

  useEffect(() => {
    let cancelled = false;
    setSignatureUrl(sourceSignatureUrl);
    quantizeImageForScreen(sourceSignatureUrl, LIST_PREVIEW_SCREEN).then((url) => {
      if (!cancelled) setSignatureUrl(url);
    });
    return () => { cancelled = true; };
  }, [sourceSignatureUrl]);

  useEffect(() => {
    let cancelled = false;
    resolveImageUrls(template.elements).then((elements) => {
      if (!cancelled) setResolvedElements(elements);
    });
    return () => { cancelled = true; };
  }, [template]);

  const resolveImageUrls = async (elements: IEslTemplateItem[]) => {
    const imageElements = elements.filter((el) => el.type === EEslTemplateElementType.Image && el.imageSrc);
    if (!imageElements.length) return elements;

    const imageKeys = imageElements.map((el) => el.imageSrc!);
    const imageUrls = await eslTemplateService.GetTemplateImageUrls(imageKeys);
    if (!imageUrls) return elements;

    const imageUrlsMap = new Map<string, string>(Object.entries(imageUrls).map(([key, value]) => [key, value]));

    return elements.map((el) => (
      el.type === EEslTemplateElementType.Image && el.imageSrc
        ? { ...el, imageSrc: imageUrlsMap.get(el.imageSrc) || el.imageSrc }
        : el
    ));
  };

  const onEslSignatureLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const aspectRatio = event.currentTarget.naturalWidth / event.currentTarget.naturalHeight;
    setSignatureWidth(SIGNATURE_HEIGHT * aspectRatio);
  };

  if (!resolvedElements) return null;

  return (
    <EslPreviewModeProvider initialMode={LIST_PREVIEW_SCREEN}>
      <Box sx={{
        width: template.width * scale,
        height: template.height * scale,
        border: '1px solid #ccc',
        overflow: 'hidden',
      }}>
        <Box sx={{
          width: template.width,
          height: template.height,
          position: 'relative',
          bgcolor: colorForScreen('#FFFFFF', LIST_PREVIEW_SCREEN),
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}>
          {resolvedElements.map((element) => (
            <Box
              key={element.id}
              sx={{
                position: 'absolute',
                left: `${element.position.left}px`,
                top: `${element.position.top}px`,
                width: `${element.dimensions.width}px`,
                height: `${element.dimensions.height}px`,
              }}
            >
              <RenderedElement element={element} />
            </Box>
          ))}
          <img
            src={signatureUrl}
            onLoad={onEslSignatureLoad}
            style={{ position: 'absolute', bottom: 0, right: 0, width: signatureWidth, height: SIGNATURE_HEIGHT }}
          />
        </Box>
      </Box>
    </EslPreviewModeProvider>
  );
};

export default EslTemplatePreview;
